import React, { useState, useCallback } from 'react';
import Row, { RowBetween } from 'components/Row';
import SocialMediaIcon from 'components/Icons/SocialMedias';
import { TYPE } from 'theme';
import styled from 'styled-components';
import { AutoColumn } from 'components/Column';
import { useHistory, useLocation } from 'react-router-dom';
import { dashboardOverviewPath } from 'constant/route-path';
import { StyledButton, StyledMissionCard } from './styled';
import { useCountdown } from 'hooks/useCountdown';
import { formatCountdown } from 'utils/timeUtils';
import { MissionType, Status } from './config';
import { SocialType } from 'constant/socialAuth';
import { formatDate, toFormattedUnitsNum } from 'common-helpers/utils';
import { UserMission, UserMissionMetaUnion } from 'gql-ops/graph/__generated__/request';
import user from 'stores/user';
import { observer } from 'mobx-react';
import { getOnePieceServerApi } from 'gql-ops';
import TooltipInfo from 'components/TooltipInfo';
import { formatHashValue } from 'utils';
import { ReloadIcon } from 'components/Icons';
import { GA_EVENT_ACTION, GA_EVENT_CATEGORY, trackEvent } from 'utils/googleAnalytics';
import { isMobile } from 'react-device-detect';
import missionStore from 'stores/missionStore';
import useSocialAuth from 'pages/Dashboard/socialProfile/useSocialAuth';
import { authStore } from 'stores/authorizationStore';
import { useToast } from 'context/ToastContext';
import { AksharFont } from 'theme/commonStyles';

const Description = styled.div`
    min-height: 60px;
    margin: 10px 0;
    flex: 1;
`;

const PointsText = styled.div`
    ${AksharFont}
    font-size: 32px;
    font-weight: 700;
    color: #5fd15c;
`;

const CountdownText = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    color: ${({ theme }) => theme.c.primary01};
    transition: opacity 0.3s ease;
    min-width: 24px;
`;

const getSocialType = (type: string): SocialType => {
    switch (type) {
        case 'DiscordJoin':
            return SocialType.Discord;
        case 'TwitterPostKeyword':
        case 'TwitterFollow':
        default:
            return SocialType.Twitter;
    }
};

const getDisplayStatus = (status: Status): Status => {
    if (status === Status.Duplicated) {
        return Status.Completed;
    }
    return status;
};

const isTweetMissionMeta = (
    meta: UserMissionMetaUnion | undefined,
): meta is UserMissionMetaUnion & { completedTwitterName: string; tweetLink: string } => {
    return meta !== undefined && 'completedTwitterName' in meta;
};

const isDiscordMissionMeta = (
    meta: UserMissionMetaUnion | undefined,
): meta is UserMissionMetaUnion & { link: string } => {
    return meta !== undefined && 'link' in meta && typeof meta.link === 'string';
};

const getDuplicatedTip = (point: string, meta?: UserMissionMetaUnion) => {
    if (!meta?.completedBy) {
        return 'This task was completed by another wallet';
    }

    if (!isTweetMissionMeta(meta)) {
        return `This task was completed by another wallet (${formatHashValue(meta.completedBy)})`;
    }

    return `@${meta.completedTwitterName} completed this task on ${
        meta.completedAt ? formatDate(new Date(meta.completedAt), 'yyyy.MM.DD') : 'someday'
    } with wallet address (${formatHashValue(meta.completedBy)})`;
};

const getSocialBoundStatus = (socialType: SocialType, opsUser: any) => {
    if (!opsUser) return false;

    switch (socialType) {
        case SocialType.Twitter:
            return !!opsUser.twitter?.name;
        case SocialType.Telegram:
            return !!opsUser.telegram?.name;
        case SocialType.Discord:
            return !!opsUser.discord?.name;
        default:
            return false;
    }
};

const checkXAppInstalled = (tweetLink: string) => {
    if (typeof window === 'undefined') return;

    // Check if device is iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
        tweetLink = tweetLink.replace('twitter.com', 'x.com');
    }

    const link = document.createElement('a');
    link.href = tweetLink;
    link.target = '_blank';
    link.rel = 'noreferrer noopener';
    link.style.display = 'none';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
};

const Mission = ({ mission, onChange }: { mission: UserMission; onChange?: (mission?: UserMission) => void }) => {
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [triggerAuth] = useSocialAuth();
    const toast = useToast();
    const { type, point, name, description, endDate, startDate, status, meta } = mission;
    const { days, hours, minutes, seconds, isExpired } = useCountdown(endDate ? new Date(endDate).getTime() : null);
    const socialType = getSocialType(type);

    const getIsSocialBound = useCallback(() => {
        return getSocialBoundStatus(socialType, user.opsUser);
    }, [socialType]);

    const isSocialBound = getIsSocialBound();

    const startCooldown = useCallback(() => {
        setCooldown(60);
        const timer = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const handleTwitterAuth = async () => {
        try {
            trackEvent({
                category: GA_EVENT_CATEGORY.User,
                action: GA_EVENT_ACTION.click_bind_social,
                label: SocialType.Twitter,
            });
            try {
                await authStore.getToken();
            } catch (error) {
                // do nothing
            }
            const authResult = await triggerAuth(SocialType.Twitter);
            await user.refreshOpsUserInfo();
            const profile = { ...user?.opsUser?.twitter, bindAddress: user.address };
            // 刷新任务列表
            await missionStore.fetchMissions();

            // 重新执行 syncMissionStatus
            if (meta?.historyId) {
                const { syncMissionStatus: syncedMission } = await getOnePieceServerApi().syncMissionStatus({
                    address: user.address,
                    historyId: meta.historyId,
                });

                const missionWithDefaults: UserMission = {
                    ...syncedMission,
                    dayIndex: mission.dayIndex,
                    seriesKey: mission.seriesKey,
                    id: Number(syncedMission.id),
                    type: syncedMission.type as MissionType,
                };

                onChange?.(missionWithDefaults);
                startCooldown();
            }
        } catch (error: any) {
            console.error(error);
            toast(error?.message || 'Oops, something went wrong.', 'warning');
        }
    };

    const onClick = async () => {
        if (isSocialBound) {
            trackEvent({
                category: GA_EVENT_CATEGORY.Engagement,
                action: GA_EVENT_ACTION.click_social_task,
            });

            if (isTweetMissionMeta(meta) && meta.tweetLink) {
                if (isMobile) {
                    checkXAppInstalled(meta.tweetLink);
                } else {
                    window.open(meta.tweetLink, '_blank');
                }
            }

            if (mission.type === 'TwitterPostKeyword') {
                const goatRollupUrl = 'https://x.com/GOATRollup';
                window.open(goatRollupUrl, '_blank');
            }
            if (mission.type === 'TwitterFollow') {
                const goatRollupUrl = 'https://twitter.com/intent/user?screen_name=GOATRollup';
                window.open(goatRollupUrl, '_blank');
            }
            if (mission.type === 'DiscordJoin') {
                // 检查用户是否绑定了Discord
                const isDiscordBound = getSocialBoundStatus(SocialType.Discord, user.opsUser);
                if (!isDiscordBound) {
                    // 如果没有绑定Discord，跳转到dashboard的Social Profile部分
                    history.push({
                        pathname: dashboardOverviewPath,
                        search: location.search,
                        state: { scrollTo: 'socialProfile' },
                    });
                    return;
                }
                // 如果已绑定Discord，打开Discord链接
                if (isDiscordMissionMeta(meta)) {
                    window.open(meta.link, '_blank');
                }
            }

            if (status === Status.NotStart) {
                try {
                    const { startMission: updatedMission } = await getOnePieceServerApi().startMission({
                        address: user.address,
                        missionId: mission.id,
                    });

                    const missionWithDefaults: UserMission = {
                        ...updatedMission,
                        dayIndex: mission.dayIndex,
                        seriesKey: mission.seriesKey,
                        id: Number(updatedMission.id),
                        type: updatedMission.type as MissionType,
                    };

                    onChange?.(missionWithDefaults);
                } catch (error) {
                    console.error('Failed to start mission:', error);
                }
            }
        } else {
            history.push({
                pathname: dashboardOverviewPath,
                search: location.search,
                state: { scrollTo: 'socialProfile' },
            });
        }
    };

    const reloadMission = async () => {
        if (!meta?.historyId || loading || cooldown > 0) return;
        if (status !== Status.InProgress) return;

        setLoading(true);
        try {
            // 先刷新任务列表
            await missionStore.fetchMissions();

            // 检查刷新后的任务状态
            const updatedMission = missionStore.missions.find((m) => m.id === mission.id);
            if (!updatedMission || updatedMission.status !== Status.InProgress) {
                startCooldown();
                return;
            }

            const { syncMissionStatus: syncedMission } = await getOnePieceServerApi().syncMissionStatus({
                address: user.address,
                historyId: meta.historyId,
            });

            const missionWithDefaults: UserMission = {
                ...syncedMission,
                dayIndex: mission.dayIndex,
                seriesKey: mission.seriesKey,
                id: Number(syncedMission.id),
                type: syncedMission.type as MissionType,
            };

            onChange?.(missionWithDefaults);
            startCooldown();
        } catch (error: any) {
            console.error('Failed to reload mission:', error);
            if (error?.code === 'TWITTER_AUTH_FAILED') {
                await handleTwitterAuth();
            }
        } finally {
            setLoading(false);
        }
    };

    const isCompleted = [Status.Completed, Status.Duplicated].includes(status as Status);

    return (
        <StyledMissionCard status={isSocialBound ? getDisplayStatus(status as Status) : undefined}>
            <Row direction="column" height="100%" align="start" gap="10px">
                <RowBetween wrap={isMobile ? 'wrap' : undefined} style={{ alignItems: 'start' }}>
                    <AutoColumn gap="4px" style={{ maxWidth: isMobile ? '180px' : '240px' }}>
                        <Row gap="10px">
                            <SocialMediaIcon type={socialType} size="sm" />
                            <div>
                                <TYPE.main fontWeight={700}>{name}</TYPE.main>
                            </div>
                        </Row>
                        <TYPE.desc fontSize={12} color="#999">
                            Mission Date: {formatDate(new Date(startDate), 'yyyy.MM.DD')}
                        </TYPE.desc>
                    </AutoColumn>
                    <AutoColumn justify="center">
                        <PointsText>+ {toFormattedUnitsNum(point)}</PointsText>
                        <Row gap="4px" style={{ justifyContent: 'center' }}>
                            {status === Status.InProgress && (
                                <TooltipInfo title={cooldown > 0 ? `Wait ${cooldown}s to refresh` : 'Click to refresh'}>
                                    <CountdownText onClick={reloadMission} disabled={cooldown > 0 || loading}>
                                        {loading ? (
                                            <ReloadIcon type="outline" spin={true} size={18} />
                                        ) : cooldown > 0 ? (
                                            cooldown
                                        ) : (
                                            <ReloadIcon type="outline" size={18} />
                                        )}
                                    </CountdownText>
                                </TooltipInfo>
                            )}
                            <TYPE.desc fontSize="12px" color="#fff">
                                Points
                            </TYPE.desc>
                        </Row>
                    </AutoColumn>
                </RowBetween>
                <TYPE.desc fontSize={12}>
                    {endDate
                        ? isExpired
                            ? 'Expired.'
                            : `Expired in ${formatCountdown(days, hours, minutes, seconds)}`
                        : ''}
                </TYPE.desc>
                <Description>
                    <TYPE.desc dangerouslySetInnerHTML={{ __html: description as string }}></TYPE.desc>
                </Description>
                {mission.type === 'DiscordJoin' ? (
                    isCompleted ? (
                        <TooltipInfo
                            title={status === Status.Duplicated ? getDuplicatedTip(point, meta) : undefined}
                            disabled={status !== Status.Duplicated}
                            style={{ width: '100%' }}
                        >
                            <StyledButton type="outlineGradient" size="unset" onClick={onClick} disabled={true}>
                                Task Completed
                            </StyledButton>
                        </TooltipInfo>
                    ) : isSocialBound ? (
                        <RowBetween gap="10px">
                            <StyledButton
                                type="outlineGradient"
                                size="unset"
                                onClick={() => {
                                    window.open('https://discord.com/invite/goatnetworkofficial', '_blank');
                                }}
                            >
                                1.Join Discord
                            </StyledButton>
                            <StyledButton type="outlineGradient" size="unset" onClick={onClick}>
                                2.Start Mission
                            </StyledButton>
                        </RowBetween>
                    ) : (
                        <TooltipInfo
                            title={status === Status.Duplicated ? getDuplicatedTip(point, meta) : undefined}
                            disabled={status !== Status.Duplicated}
                            style={{ width: '100%' }}
                        >
                            <StyledButton type="outlineGradient" size="unset" onClick={onClick}>
                                Bind Your Social Account
                            </StyledButton>
                        </TooltipInfo>
                    )
                ) : (
                    <TooltipInfo
                        title={status === Status.Duplicated ? getDuplicatedTip(point, meta) : undefined}
                        disabled={status !== Status.Duplicated}
                        style={{ width: '100%' }}
                    >
                        <StyledButton type="outlineGradient" size="unset" onClick={onClick} disabled={isCompleted}>
                            {isCompleted
                                ? 'Task Completed'
                                : isSocialBound
                                ? 'Start Mission'
                                : 'Bind Your Social Account'}
                        </StyledButton>
                    </TooltipInfo>
                )}
            </Row>
        </StyledMissionCard>
    );
};

export default observer(Mission);
